import { useState, useEffect, useRef } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import { Building, MapPin, User, Filter, Search, X } from "lucide-react";
import Swal from "sweetalert2";
import {
  obtenerTorres,
  Torre,
} from "../../../../api/torreService";
import {
  obtenerApartamentos,
  obtenerApartamentosConPropietarios,
  Apartamento,
  ApartamentoConPropietario,
} from "../../../../api/apartamentoService";
import EnviarLlaveModal from "./EnviarLlaveModal";
import VerPropietarioModal from "./VerPropietarioModal";

export default function ApartamentosPage() {
  const { session } = useAuth();
  
  const isLoadingCatalogosRef = useRef(false);
  const isLoadingApartamentosRef = useRef(false);
  const catalogosLoadedRef = useRef(false);
  
  const [apartamentos, setApartamentos] = useState<ApartamentoConPropietario[]>([]);
  const [filteredApartamentos, setFilteredApartamentos] = useState<ApartamentoConPropietario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamentoFiltro, setSelectedApartamentoFiltro] = useState("");
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentosFiltro, setApartamentosFiltro] = useState<Apartamento[]>([]);
  const [selectedApartamento, setSelectedApartamento] = useState<ApartamentoConPropietario | null>(null);
  const [showEnviarLlaveModal, setShowEnviarLlaveModal] = useState(false);
  const [showVerPropietarioModal, setShowVerPropietarioModal] = useState(false);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    
    if (unidadId && !catalogosLoadedRef.current && !isLoadingCatalogosRef.current) {
      catalogosLoadedRef.current = true;
      loadCatalogos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.unidad?.id]);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    
    if (unidadId) {
      loadApartamentosConPropietarios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.unidad?.id, selectedTorre, selectedApartamentoFiltro]);

  useEffect(() => {
    filterApartamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, apartamentos]);

  const loadCatalogos = async () => {
    if (isLoadingCatalogosRef.current) return;

    try {
      isLoadingCatalogosRef.current = true;
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });

      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los catálogos",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      isLoadingCatalogosRef.current = false;
    }
  };

  const loadApartamentosFiltro = async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentosFiltro([]);
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentosFiltro([]);
        return;
      }

      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentosFiltro(response.object_);
      } else {
        setApartamentosFiltro([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentosFiltro([]);
    }
  };

  const loadApartamentosConPropietarios = async () => {
    if (isLoadingApartamentosRef.current) return;

    try {
      isLoadingApartamentosRef.current = true;
      setIsLoading(true);
      
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentos([]);
        setFilteredApartamentos([]);
        return;
      }

      const payload = {
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": selectedTorre || "",
        "apartamento codigo": selectedApartamentoFiltro || "",
      };

      const response = await obtenerApartamentosConPropietarios(payload);

      if (response.code === "1" && response.object_) {
        const sorted = response.object_.sort((a, b) => {
          const torreA = (a.apt_torre_nombre || "").toLowerCase();
          const torreB = (b.apt_torre_nombre || "").toLowerCase();
          if (torreA !== torreB) {
            return torreA.localeCompare(torreB);
          }
          const aptA = (a.apt_nombre || "").toLowerCase();
          const aptB = (b.apt_nombre || "").toLowerCase();
          return aptA.localeCompare(aptB);
        });
        setApartamentos(sorted);
        setFilteredApartamentos(sorted);
      } else {
        setApartamentos([]);
        setFilteredApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      const errorMessage = error instanceof Error ? error.message : "No se pudieron cargar los apartamentos";
      
      Swal.fire({
        icon: "error",
        title: "Error al cargar apartamentos",
        text: errorMessage,
        confirmButtonColor: "#3d5a40",
      });
      
      setApartamentos([]);
      setFilteredApartamentos([]);
    } finally {
      isLoadingApartamentosRef.current = false;
      setIsLoading(false);
    }
  };

  const filterApartamentos = () => {
    if (!searchTerm.trim()) {
      setFilteredApartamentos(apartamentos);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = apartamentos.filter(
      (apt) =>
        (apt.apt_nombre || "").toLowerCase().includes(term) ||
        (apt.apt_torre_nombre || "").toLowerCase().includes(term) ||
        (apt.apt_usuario_propietario || "").toLowerCase().includes(term) ||
        (apt.apt_unidad_residencial_nombre || "").toLowerCase().includes(term)
    );
    setFilteredApartamentos(filtered);
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamentoFiltro("");
    setApartamentosFiltro([]);
    
    if (value) {
      loadApartamentosFiltro(value);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTorre("");
    setSelectedApartamentoFiltro("");
    setApartamentosFiltro([]);
  };

  const handleApartamentoClick = (apt: ApartamentoConPropietario) => {
    setSelectedApartamento(apt);
    if (!apt.apt_usuario_propietario) {
      setShowEnviarLlaveModal(true);
    } else {
      setShowVerPropietarioModal(true);
    }
  };

  const handleCloseEnviarLlave = () => {
    setShowEnviarLlaveModal(false);
    setSelectedApartamento(null);
  };

  const handleCloseVerPropietario = () => {
    setShowVerPropietarioModal(false);
    setSelectedApartamento(null);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
      <Sidebar />

      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-military-600 flex items-center gap-2 sm:gap-3">
                <Building size={24} className="sm:w-8 sm:h-8" />
                Gestión de Apartamentos
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Consulta los apartamentos y sus propietarios
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 sm:mt-6">
            <Card className="border-l-4 border-military-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Apartamentos</p>
                    <p className="text-2xl font-bold text-gray-900">{apartamentos.length}</p>
                  </div>
                  <div className="bg-military-100 p-3 rounded-full">
                    <Building size={24} className="text-military-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Con Propietario</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {apartamentos.filter((apt) => apt.apt_usuario_propietario).length}
                    </p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <User size={24} className="text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Torres</p>
                    <p className="text-2xl font-bold text-green-600">
                      {torres.length}
                    </p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <Building size={24} className="text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-6 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Filter size={20} />
                Filtros
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-600"
                >
                  <X size={16} className="mr-1" />
                  Limpiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-gray-600"
                >
                  {showFilters ? "Ocultar" : "Mostrar"}
                </Button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search size={16} className="inline mr-1" />
                    Buscar
                  </label>
                  <Input
                    placeholder="Apartamento, torre, email propietario..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building size={16} className="inline mr-1" />
                    Torre
                  </label>
                  <select
                    value={selectedTorre}
                    onChange={(e) => handleTorreChange(e.target.value)}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                  >
                    <option value="">Todas las torres</option>
                    {torres.map((torre) => (
                      <option key={torre.tor_codigo} value={torre.tor_codigo}>
                        {torre.tor_nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin size={16} className="inline mr-1" />
                    Apartamento
                  </label>
                  <select
                    value={selectedApartamentoFiltro}
                    onChange={(e) => setSelectedApartamentoFiltro(e.target.value)}
                    disabled={!selectedTorre}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Todos los apartamentos</option>
                    {apartamentosFiltro.map((apt) => (
                      <option key={apt.apt_codigo} value={apt.apt_codigo}>
                        {apt.apt_nombre}
                      </option>
                    ))}
                  </select>
                  {!selectedTorre && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selecciona una torre primero
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Apartamentos ({filteredApartamentos.length})
            </h3>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                <p className="text-gray-600">Cargando apartamentos...</p>
              </div>
            ) : filteredApartamentos.length === 0 ? (
              <div className="text-center py-12">
                <Building size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron apartamentos</p>
                <p className="text-sm text-gray-500 mt-2">
                  {searchTerm || selectedTorre || selectedApartamentoFiltro
                    ? "Intenta ajustar los filtros"
                    : "No hay apartamentos disponibles"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Torre</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Apartamento</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Piso</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Propietario (Email)</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Estatus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApartamentos.map((apt, index) => (
                      <tr
                        key={`${apt.apt_codigo}-${index}`}
                        onClick={() => handleApartamentoClick(apt)}
                        className={`border-b border-gray-200 transition-colors ${
                          !apt.apt_usuario_propietario 
                            ? "hover:bg-blue-50 cursor-pointer" 
                            : "hover:bg-gray-50 cursor-pointer"
                        }`}
                      >
                        <td className="p-3 text-sm text-gray-900">
                          {apt.apt_torre_nombre || "N/A"}
                        </td>
                        <td className="p-3 text-sm font-medium text-gray-900">
                          {apt.apt_nombre}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {apt.apt_piso !== undefined ? apt.apt_piso : "-"}
                        </td>
                        <td className="p-3 text-sm text-gray-700">
                          {apt.apt_usuario_propietario ? (
                            <a 
                              href={`mailto:${apt.apt_usuario_propietario}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {apt.apt_usuario_propietario}
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">Sin propietario</span>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {apt.apt_estatus === "A" ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              Activo
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {apt.apt_estatus || "N/A"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {showEnviarLlaveModal && selectedApartamento && (
        <EnviarLlaveModal
          isOpen={showEnviarLlaveModal}
          apartamento={selectedApartamento}
          onClose={handleCloseEnviarLlave}
        />
      )}

      {showVerPropietarioModal && selectedApartamento && (
        <VerPropietarioModal
          isOpen={showVerPropietarioModal}
          apartamento={selectedApartamento}
          onClose={handleCloseVerPropietario}
        />
      )}
    </div>
  );
}

